// frontend/src/components/marketplace/ReportModal.jsx
import React, { useState } from 'react';
import { Flag, X, Loader2, ImagePlus, LinkIcon, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'ivisora_auth_token';

const CREATOR_REASONS = [
  { value: 'copyright_violation', label: 'Copyright violation' },
  { value: 'stolen_design', label: 'Stolen design' },
  { value: 'trademark_issue', label: 'Trademark issue' },
  { value: 'spam', label: 'Spam' },
  { value: 'fraud', label: 'Fraud' },
  { value: 'other', label: 'Other' },
];
const TEMPLATE_REASONS = [
  { value: 'copied_design', label: 'Copied design' },
  { value: 'trademark_violation', label: 'Trademark violation' },
  { value: 'copyright_issue', label: 'Copyright issue' },
  { value: 'spam', label: 'Spam' },
  { value: 'other', label: 'Other' },
];

/**
 * type: "creator" | "template"; targetId: id; onClose: () => void
 */
const ReportModal = ({ type, targetId, onClose }) => {
  const reasons = type === 'creator' ? CREATOR_REASONS : TEMPLATE_REASONS;
  const [reason, setReason] = useState(reasons[0].value);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // 3.5-EXT evidence state
  const [evidenceUrls, setEvidenceUrls] = useState([]);   // uploaded screenshot URLs
  const [evidenceLinks, setEvidenceLinks] = useState([]); // reference URLs
  const [evidenceNotes, setEvidenceNotes] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [linkError, setLinkError] = useState('');

  const handleFiles = async (e) => {
    setError('');
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setError('Please sign in to attach evidence.'); return; }
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const r = await fetch(`${API_URL}/api/uploads`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) {
          throw new Error(typeof d.detail === 'string' ? d.detail : 'Failed to upload screenshot');
        }
        if (d.url) uploaded.push(d.url);
      }
      if (uploaded.length) setEvidenceUrls((prev) => [...prev, ...uploaded]);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      // allow re-selecting the same file
      e.target.value = '';
    }
  };

  const removeUrl = (idx) => {
    setEvidenceUrls((prev) => prev.filter((_, i) => i !== idx));
  };

  const addLink = () => {
    setLinkError('');
    const v = linkInput.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) {
      setLinkError('Links must start with http:// or https://');
      return;
    }
    setEvidenceLinks((prev) => [...prev, v]);
    setLinkInput('');
  };

  const removeLink = (idx) => {
    setEvidenceLinks((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    setError('');
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setError('Please sign in to submit a report.'); return; }
    setSubmitting(true);
    try {
      const path = type === 'creator'
        ? `/api/marketplace/creators/${targetId}/report`
        : `/api/marketplace/templates/${targetId}/report`;
      const r = await fetch(`${API_URL}${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason,
          description,
          evidence_urls: evidenceUrls,
          evidence_links: evidenceLinks,
          evidence_notes: evidenceNotes,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (r.status === 429) {
          throw new Error("You're reporting too frequently. Please try again later.");
        }
        if (r.status === 409) {
          throw new Error(
            typeof d.detail === 'string'
              ? d.detail
              : 'You already have an open report for this.'
          );
        }
        throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not submit report');
      }
      setDone(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Flag className="w-4 h-4 text-red-500" /> Report {type}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        {done ? (
          <div className="py-6 text-center">
            <p className="text-sm text-gray-700">Thanks — your report has been submitted for review.</p>
            <button onClick={onClose} className="mt-4 px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">Tell us what's wrong. Our team will investigate.</p>
            {error && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">{error}</div>}

            <label className="block text-sm font-medium text-gray-700 mb-1.5">Reason</label>
            <select value={reason} onChange={e => setReason(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm mb-4">
              {reasons.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1.5">Details</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
                      placeholder="Optional context…"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none mb-4" />

            {/* Screenshots */}
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Screenshots</label>
            <label className="flex items-center gap-2 w-full px-4 py-2.5 border border-dashed border-gray-300 rounded-xl text-sm text-gray-500 cursor-pointer hover:border-violet-300 hover:text-violet-600 mb-2">
              {uploading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                : <><ImagePlus className="w-4 h-4" /> Add screenshots</>}
              <input type="file" accept="image/*" multiple onChange={handleFiles} disabled={uploading} className="hidden" />
            </label>
            {evidenceUrls.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {evidenceUrls.map((u, idx) => (
                  <div key={`${u}-${idx}`} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                    <img src={u} alt="evidence" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeUrl(idx)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                      aria-label="Remove screenshot"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Reference URLs */}
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Reference URLs</label>
            <div className="flex gap-2 mb-1">
              <input
                type="text"
                value={linkInput}
                onChange={e => setLinkInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }}
                placeholder="https://…"
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
              />
              <button
                type="button"
                onClick={addLink}
                className="flex items-center gap-1 px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            {linkError && <p className="text-xs text-red-500 mb-2">{linkError}</p>}
            {evidenceLinks.length > 0 && (
              <ul className="space-y-1.5 mb-4">
                {evidenceLinks.map((l, idx) => (
                  <li key={`${l}-${idx}`} className="flex items-center gap-2 text-sm">
                    <LinkIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate text-violet-600">{l}</span>
                    <button
                      type="button"
                      onClick={() => removeLink(idx)}
                      className="ml-auto text-gray-400 hover:text-red-500 flex-shrink-0"
                      aria-label="Remove link"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Notes */}
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
            <textarea value={evidenceNotes} onChange={e => setEvidenceNotes(e.target.value)} rows={2}
                      placeholder="Anything else we should know…"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none mb-4" />

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={submit} disabled={submitting || uploading} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : 'Submit report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportModal;
